import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import FlexService from '../../services/FlexService';
import { resolveAdvancedSearchFieldMeta, withCustomFieldKeys, mergeStatusOptions } from './workspaceUtils';
import { ENTITY_FIELDS_CHANGED } from './AddCustomFieldDialog';

/**
 * The company's own values for status fields that read them from a list (FH-318), merged into the fields'
 * status mappings. A list that fails to load leaves the field as it was.
 */
const withStatusOptions = async (fields) => {
  const listed = (fields || []).filter((f) => f.statusMapping && f.optionsScript);
  if (listed.length === 0) return fields;
  const optionsByField = {};
  await Promise.all(listed.map(async (f) => {
    try {
      const res = await FlexService.executeScript({ scriptName: f.optionsScript, context: {} });
      const list = res?.context?.result;
      if (res?.success && Array.isArray(list)) optionsByField[f.fieldName] = list;
    } catch (err) {
      console.error(`Failed to load the values of ${f.fieldName} from ${f.optionsScript}:`, err);
    }
  }));
  return mergeStatusOptions(fields, optionsByField);
};

/**
 * Hook for workspace data fetching, filtering, and state management.
 */
const useWorkspaceData = (views, activeViewId, setActiveViewId) => {
  const activeView = views.find((v) => v.viewId === activeViewId);

  // Grid state
  const [masterData, setMasterData] = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const [masterError, setMasterError] = useState(null);
  const masterLoadGeneration = useRef(0);
  // Server paging (FH-204): the list's real total, the batch being added, and the params and sort
  // the rows were loaded with
  const [masterTotal, setMasterTotal] = useState(null);
  const [masterLoadingMore, setMasterLoadingMore] = useState(false);
  const masterParamsRef = useRef({});
  const masterSortRef = useRef(null);
  const masterDataRef = useRef([]);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [detailData, setDetailData] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState(0);

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [advancedSearchParams, setAdvancedSearchParams] = useState({});

  // Navigation params
  const pendingNavParamsRef = useRef(null);
  const navForViewRef = useRef(null);
  const [navParams, setNavParams] = useState(null);

  // Entity metadata cache
  const [fieldMetaCache, setFieldMetaCache] = useState({});

  // View-scoped config
  const viewToolbar = activeView?.toolbar || null;
  const viewFilters = activeView?.filters || null;
  const viewActions = activeView?.actions || {};
  const viewStatusBar = activeView?.statusBar || null;

  // Detail config
  const resolvedDetails = useMemo(() => {
    if (!activeView) return [];
    if (activeView.details && Array.isArray(activeView.details)) return activeView.details;
    if (activeView.detail) return [activeView.detail];
    return [];
  }, [activeView]);

  // Fetch entity field metadata
  const fetchFieldMeta = useCallback(async (entityName) => {
    if (!entityName) return [];
    if (fieldMetaCache[entityName]) return fieldMetaCache[entityName];
    try {
      const token = localStorage.getItem('waide_access_token');
      const res = await fetch(`/api/v1/catalog/${entityName}/fields`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      const data = await res.json();
      const fields = await withStatusOptions(data.fields || data || []);
      setFieldMetaCache((prev) => ({ ...prev, [entityName]: fields }));
      return fields;
    } catch (err) {
      console.error(`Failed to fetch field metadata for ${entityName}:`, err);
      return [];
    }
  }, [fieldMetaCache]);

  // A layout view's grids load their own rows, so nothing above fetches their entities' fields:
  // fetch them here, so those grids get the customer's own fields as columns too (FH-202)
  useEffect(() => {
    if (!activeView?.layout) return;
    const entities = new Set();
    const walk = (node) => {
      if (Array.isArray(node)) { node.forEach(walk); return; }
      if (!node || typeof node !== 'object') return;
      if (node.type === 'grid' && typeof node.entity === 'string') entities.add(node.entity);
      Object.values(node).forEach(walk);
    };
    walk(activeView);
    entities.forEach((e) => { fetchFieldMeta(e); });
  }, [activeView]); // eslint-disable-line react-hooks/exhaustive-deps

  // A field was added to an entity (FH-202): re-read that entity's fields if this workspace has them
  useEffect(() => {
    const onFieldsChanged = async (e) => {
      const name = e.detail?.entityName;
      if (!name) return;
      try {
        const token = localStorage.getItem('waide_access_token');
        const res = await fetch(`/api/v1/catalog/${name}/fields`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        const fields = await withStatusOptions(data.fields || data || []);
        setFieldMetaCache((prev) => (prev[name] ? { ...prev, [name]: fields } : prev));
      } catch (err) {
        console.error(`Failed to re-read field metadata for ${name}:`, err);
      }
    };
    window.addEventListener(ENTITY_FIELDS_CHANGED, onFieldsChanged);
    return () => window.removeEventListener(ENTITY_FIELDS_CHANGED, onFieldsChanged);
  }, []);

  // Fetch data via FLEX loadScript: the rows, and the list's total when the Command reports one
  const fetchScriptPage = useCallback(async (loadScript, params) => {
    if (!loadScript) return { rows: [], total: null };
    try {
      const context = {};
      if (params && Object.keys(params).length > 0) {
        context._queryParams = params;
      }
      const response = await FlexService.executeScript({ scriptName: loadScript, context });
      if (!response.success) {
        throw new Error(response.errorMessage || response.message || `Failed to load ${loadScript}`);
      }
      const ctx = response.context || {};
      const total = typeof ctx.total === 'number' ? ctx.total : null;
      const data = ctx.result || ctx.data;
      if (!data) return { rows: [], total };
      if (Array.isArray(data)) return { rows: data.map(withCustomFieldKeys), total };
      if (typeof data === 'object') {
        if (Array.isArray(data.data)) return { rows: data.data.map(withCustomFieldKeys), total };
        return { rows: [withCustomFieldKeys(data.data || data)], total };
      }
      return { rows: [], total };
    } catch (err) {
      console.error(`Failed to fetch ${loadScript}:`, err);
      throw err;
    }
  }, []);
  const fetchScriptData = useCallback(async (loadScript, params) => (await fetchScriptPage(loadScript, params)).rows, [fetchScriptPage]);

  // Build merged params from chip filters + search + advanced search
  const buildMergedParams = useCallback(() => {
    const merged = {};
    if (activeFilters.length > 0) {
      const byField = {};
      activeFilters.forEach(({ field, value }) => {
        if (!byField[field]) byField[field] = [];
        byField[field].push(String(value));
      });
      Object.entries(byField).forEach(([field, values]) => {
        merged[field] = values.join(',');
      });
    }
    if (searchQuery.trim()) merged.q = searchQuery.trim();
    if (Object.keys(advancedSearchParams).length > 0) Object.assign(merged, advancedSearchParams);
    return merged;
  }, [activeFilters, searchQuery, advancedSearchParams]);

  // Load master grid data
  // opts.reset (view/filter/search change): start clean with a spinner.
  // Otherwise (refresh after an edit or action): swap the data in place so the grid
  // keeps its page, scroll, sort and the expanded row with its child grids.
  const loadMasterData = useCallback(async (opts) => {
    if (!activeView?.master) return;
    const { loadScript, entity } = activeView.master;
    if (!loadScript) return;

    const generation = ++masterLoadGeneration.current;
    setMasterError(null);
    const reset = !!(opts && opts.reset === true);
    if (reset) {
      setMasterLoading(true);
      setExpandedRowId(null);
      setDetailData({});
    }
    try {
      const mergedParams = { ...(activeView.master.loadParams || {}), ...buildMergedParams(), ...(masterSortRef.current || {}) };
      // a refresh after an action reloads as many rows as are loaded, so a later page stays put
      const loaded = masterDataRef.current.length;
      if (!reset && loaded > 100) mergedParams.limit = loaded;
      masterParamsRef.current = mergedParams;
      const { rows: data, total } = await fetchScriptPage(loadScript, mergedParams);
      if (generation !== masterLoadGeneration.current) return;
      masterDataRef.current = data;
      setMasterData(data);
      setMasterTotal(total);
      // always: a grid that declares its columns still gets the customer's own fields (FH-202)
      if (entity) await fetchFieldMeta(entity);
    } catch (err) {
      console.error('Failed to load master data:', err);
      if (generation === masterLoadGeneration.current) {
        masterDataRef.current = [];
        setMasterTotal(null);
        setMasterData([]);
        setMasterError(err.message || 'Could not load workspace data');
      }
    } finally {
      if (generation === masterLoadGeneration.current) setMasterLoading(false);
    }
  }, [activeView, fetchScriptPage, fetchFieldMeta, buildMergedParams]);

  // The next batch of a paged list, up to the row the user paged to (FH-204)
  const loadMoreMaster = useCallback(async (upTo) => {
    const loadScript = activeView?.master?.loadScript;
    const have = masterDataRef.current.length;
    if (!loadScript || have >= upTo) return;
    const generation = masterLoadGeneration.current;
    setMasterLoadingMore(true);
    try {
      const { limit, offset, ...base } = masterParamsRef.current; // eslint-disable-line no-unused-vars
      const { rows, total } = await fetchScriptPage(loadScript, { ...base, offset: have, limit: Math.max(100, upTo - have) });
      if (generation !== masterLoadGeneration.current) return; // reloaded meanwhile
      const key = activeView.master.primaryKey;
      const seen = new Set(key ? masterDataRef.current.map((r) => String(r[key])) : []);
      const next = [...masterDataRef.current, ...rows.filter((r) => !key || !seen.has(String(r[key])))];
      masterDataRef.current = next;
      setMasterData(next);
      if (total != null) setMasterTotal(total);
    } catch (err) {
      console.error('Failed to load more rows:', err);
    } finally {
      setMasterLoadingMore(false);
    }
  }, [activeView, fetchScriptPage]);

  // Sort the whole list on the server when only part of it is loaded
  const sortMaster = useCallback((column, direction) => {
    masterSortRef.current = { sort: column, order: direction };
    masterDataRef.current = [];
    loadMasterData();
  }, [loadMasterData]);

  // Export the FULL filtered result set (not just the currently-loaded page --
  // there is no client-side pagination today, but WMS list endpoints each cap
  // rows server-side under their own, inconsistent param name: some read
  // `limit` (e.g. FastOrderController, default 100), others a different name
  // entirely. There is no single universal override, so this passes the
  // common ones with a generous cap; an endpoint that recognizes none of them
  // still returns its own default-capped result rather than failing, which is
  // the same behavior the grid already has today -- just surfaced honestly
  // here rather than silently relied upon.
  const exportMasterData = useCallback(async () => {
    if (!activeView?.master?.loadScript) return [];
    const { loadScript } = activeView.master;
    const mergedParams = {
      ...(activeView.master.loadParams || {}),
      ...buildMergedParams(),
      limit: 100000,
      max: 100000,
      pageSize: 100000,
    };
    return fetchScriptData(loadScript, mergedParams);
  }, [activeView, fetchScriptData, buildMergedParams]);

  // Load detail data for expanded row
  const loadDetailData = useCallback(async (rowData) => {
    if (resolvedDetails.length === 0) return;
    setDetailLoading(true);
    try {
      const results = {};
      await Promise.all(resolvedDetails.map(async (det) => {
        const { loadScript, entity, parentKey, foreignKey, responseKey, _parentParamsMap } = det;
        if (!loadScript) return;
        const parentValue = rowData[parentKey];
        if (!parentValue) return;
        let params;
        if (_parentParamsMap) {
          params = {};
          for (const [scriptParam, parentField] of Object.entries(_parentParamsMap)) {
            params[scriptParam] = rowData[parentField];
          }
        } else {
          params = { [foreignKey]: parentValue };
        }
        // A detail grid shows all of its parent's rows: when the Command reports more than one batch
        // (total), fetch the rest, so a receipt's 205 lines aren't cut at 200 (FH-257)
        const firstPage = await fetchScriptPage(loadScript, params);
        let data = firstPage.rows;
        for (let guard = 0; firstPage.total != null && data.length < firstPage.total && guard < 200; guard += 1) {
          const more = await fetchScriptPage(loadScript, { ...params, offset: data.length, limit: 500 });
          if (!more.rows.length) break;
          data = [...data, ...more.rows];
        }
        if (responseKey && !Array.isArray(data) && data && data[0]) {
          data = data[0][responseKey] || [];
        } else if (responseKey && Array.isArray(data) && data.length === 1) {
          data = data[0][responseKey] || [];
        }
        const detKey = entity || loadScript;
        if (!results[parentValue]) results[parentValue] = {};
        results[parentValue][detKey] = Array.isArray(data) ? data : [];
        if (entity) await fetchFieldMeta(entity); // for its custom field columns too (FH-202)
      }));
      setDetailData((prev) => {
        const next = { ...prev };
        for (const [pv, entityMap] of Object.entries(results)) {
          next[pv] = { ...(next[pv] || {}), ...entityMap };
        }
        return next;
      });
    } catch (err) {
      console.error('Failed to load detail data:', err);
    } finally {
      setDetailLoading(false);
    }
  }, [resolvedDetails, fetchScriptPage, fetchFieldMeta]);

  // Initialize default chip filters when view changes
  useEffect(() => {
    if (viewFilters?.chips) {
      const defaults = viewFilters.chips.filter((c) => c.default).map((c) => ({ field: c.field, value: c.value }));
      setActiveFilters(defaults);
    } else {
      setActiveFilters([]);
    }
    // the values a view was opened with are kept for that view: React runs this twice on mount in development,
    // and the second run must not lose what the first one took
    if (navForViewRef.current && navForViewRef.current.viewId !== activeViewId) navForViewRef.current = null;
    if (pendingNavParamsRef.current) {
      navForViewRef.current = { viewId: activeViewId, params: pendingNavParamsRef.current };
      pendingNavParamsRef.current = null;
    }
    const opened = navForViewRef.current?.viewId === activeViewId ? navForViewRef.current.params : null;
    setAdvancedSearchParams(opened || {});
    setNavParams(opened);
    setSearchQuery('');
    masterSortRef.current = null; // another view: its own order
  }, [activeViewId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load data when view changes or filters change
  useEffect(() => {
    if (activeView?.master && activeView.master.type !== 'orderable-list') {
      loadMasterData({ reset: true });
    }
  }, [activeViewId, activeFilters, advancedSearchParams, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Status bar counters
  const statusCounters = useMemo(() => {
    if (!viewStatusBar?.counters) return [];
    // only part of the list loaded (FH-204): the total is the server's; a per-value count can't be
    // known from part of the list, so it shows as unknown rather than a wrong number
    const partial = masterTotal != null && masterTotal > masterData.length;
    return viewStatusBar.counters.map((counter) => {
      if (counter.type === 'total' || !counter.field) return { ...counter, count: masterTotal ?? masterData.length };
      if (partial) return { ...counter, count: null };
      return { ...counter, count: masterData.filter((row) => String(row[counter.field]) === String(counter.value)).length };
    });
  }, [viewStatusBar, masterData, masterTotal]);

  // Advanced search field metadata
  const advancedSearchFieldMeta = useMemo(
    () => resolveAdvancedSearchFieldMeta(viewToolbar, fieldMetaCache, activeView?.master?.entity),
    [activeView, fieldMetaCache, viewToolbar]
  );

  return {
    activeView,
    masterData,
    masterLoading,
    masterError,
    expandedRowId,
    setExpandedRowId,
    detailData,
    detailLoading,
    activeDetailTab,
    setActiveDetailTab,
    searchQuery,
    setSearchQuery,
    activeFilters,
    setActiveFilters,
    advancedSearchParams,
    setAdvancedSearchParams,
    navParams,
    setNavParams,
    pendingNavParamsRef,
    fieldMetaCache,
    viewToolbar,
    viewFilters,
    viewActions,
    resolvedDetails,
    loadMasterData,
    masterTotal,
    masterLoadingMore,
    loadMoreMaster,
    sortMaster,
    loadDetailData,
    exportMasterData,
    fetchFieldMeta,
    statusCounters,
    advancedSearchFieldMeta,
  };
};

export default useWorkspaceData;
